import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Store, Mail, Lock, User, Phone, MapPin, Building2, 
  CheckCircle, AlertCircle, ArrowRight, ArrowLeft, Loader2
} from 'lucide-react';
import api from '@/lib/api';

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️', description: 'Food service and dining' },
  { value: 'grocery', label: 'Grocery Store', icon: '🛒', description: 'Food and household items' },
  { value: 'service', label: 'Service Provider', icon: '🔧', description: 'Professional services' }
];

export default function VendorSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Business Type
    business_type: '',
    
    // Step 2: Business Info
    business_name: '',
    business_description: '',
    business_category: '',
    business_phone: '',
    business_address: '',
    
    // Step 3: Account Info
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    password: '',
    confirm_password: ''
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const validateStep = () => {
    setError('');
    
    if (step === 1 && !formData.business_type) {
      setError('Please select a business type');
      return false;
    }
    
    if (step === 2) {
      if (!formData.business_name || !formData.business_phone || !formData.business_address) {
        setError('Please fill in all required business information');
        return false;
      }
    }
    
    if (step === 3) {
      if (!formData.owner_name || !formData.owner_email || !formData.password) {
        setError('Please fill in all required account information');
        return false;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      if (formData.password !== formData.confirm_password) {
        setError('Passwords do not match');
        return false;
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError('');

    try {
      // Register account
      const registerData = {
        email: formData.owner_email,
        password: formData.password,
        name: formData.owner_name,
        phone: formData.owner_phone || formData.business_phone,
        role: 'vendor'
      };

      await api.post('/auth/register', registerData);

      // Login to get token
      const loginRes = await api.post('/auth/login', {
        email: formData.owner_email,
        password: formData.password
      });

      const token = loginRes.data.access_token;
      localStorage.setItem('r2yn_token', token);
      localStorage.setItem('r2yn_user', JSON.stringify(loginRes.data.user));

      // Create vendor profile via onboarding
      const onboardingData = {
        data: {
          business_name: formData.business_name,
          business_type: formData.business_type,
          description: formData.business_description,
          category: formData.business_category || getBuinessTypeLabel(formData.business_type),
          phone: formData.business_phone,
          address: formData.business_address
        }
      };

      await api.post('/vendor/onboarding/step/1', onboardingData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Account created successfully! Welcome to R2YN.');
      navigate('/vendor/onboarding');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getBuinessTypeLabel = (type) => {
    const bt = BUSINESS_TYPES.find(b => b.value === type);
    return bt ? bt.label : type;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/r2yn-logo-color.png" 
            alt="R2YN" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold" data-font="display">Join R2YN as a Vendor</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Start selling on our platform in minutes
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                Vendor Registration
              </CardTitle>
              <Badge variant="outline">Step {step} of {totalSteps}</Badge>
            </div>
            <Progress value={progress} className="h-2" />
          </CardHeader>

          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Step 1: Business Type */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Select Your Business Type</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose the category that best describes your business
                  </p>
                </div>

                <div className="grid gap-4">
                  {BUSINESS_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, business_type: type.value })}
                      className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                        formData.business_type === type.value 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border'
                      }`}
                      data-testid={`business-type-${type.value}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="text-3xl">{type.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{type.label}</h4>
                            {formData.business_type === type.value && (
                              <CheckCircle className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Business Info */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Business Information</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Tell us about your {getBuinessTypeLabel(formData.business_type).toLowerCase()}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      placeholder="Your Business Name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      data-testid="business-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="business_description">Description (optional)</Label>
                    <Textarea
                      id="business_description"
                      placeholder="Describe your business..."
                      value={formData.business_description}
                      onChange={(e) => setFormData({ ...formData, business_description: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="business_phone">Business Phone *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="business_phone"
                        type="tel"
                        placeholder="+1-555-0123"
                        value={formData.business_phone}
                        onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                        className="pl-10"
                        data-testid="business-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="business_address">Business Address *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="business_address"
                        placeholder="123 Main St, City, State ZIP"
                        value={formData.business_address}
                        onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                        className="pl-10"
                        data-testid="business-address-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Account Info */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Create Your Account</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set up your login credentials
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="owner_name">Your Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="owner_name"
                        placeholder="John Doe"
                        value={formData.owner_name}
                        onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                        className="pl-10"
                        data-testid="owner-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="owner_email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="owner_email"
                        type="email"
                        placeholder="you@yourbusiness.com"
                        value={formData.owner_email}
                        onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                        className="pl-10"
                        data-testid="owner-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="At least 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10"
                        data-testid="password-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirm Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm_password"
                        type="password"
                        placeholder="Re-enter password"
                        value={formData.confirm_password}
                        onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                        className="pl-10"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/vendor/login')}
                >
                  Already have an account?
                </Button>
              )}

              {step < totalSteps ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  data-testid="next-button"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <CheckCircle className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
